const STORAGE_KEY_PREFIX = 'fts.proctoring.timer.'

type ProctoringTimerSnapshot = {
    referenceId: string
    remainingSeconds: number
    updatedAt: number
}

const storageKey = (referenceId: string) => `${STORAGE_KEY_PREFIX}${referenceId}`

const canUseLocalStorage = () => {
    try {
        return typeof localStorage !== 'undefined'
    } catch {
        return false
    }
}

export const readProctoringTimerSnapshot = (
    referenceId: string
): ProctoringTimerSnapshot | null => {
    if (!referenceId || !canUseLocalStorage()) return null
    try {
        const raw = localStorage.getItem(storageKey(referenceId))
        if (!raw) return null
        const parsed = JSON.parse(raw) as Partial<ProctoringTimerSnapshot>
        if (
            parsed.referenceId !== referenceId ||
            typeof parsed.remainingSeconds !== 'number' ||
            typeof parsed.updatedAt !== 'number'
        ) {
            return null
        }
        return {
            referenceId: parsed.referenceId,
            remainingSeconds: parsed.remainingSeconds,
            updatedAt: parsed.updatedAt,
        }
    } catch {
        return null
    }
}

/**
 * Returns remaining seconds for this reference, adjusting for wall-clock time
 * spent away (refresh, tab close, permission revoke, tab background).
 */
export const readProctoringTimerRemaining = (
    referenceId: string,
    durationSeconds: number,
    now = Date.now()
): number | null => {
    const snapshot = readProctoringTimerSnapshot(referenceId)
    if (!snapshot) return null

    const elapsed = Math.floor((now - snapshot.updatedAt) / 1000)
    const remaining = snapshot.remainingSeconds - Math.max(0, elapsed)
    return Math.max(0, Math.min(durationSeconds, Math.floor(remaining)))
}

export const writeProctoringTimerSnapshot = (
    referenceId: string,
    remainingSeconds: number,
    now = Date.now()
): void => {
    if (!referenceId || !canUseLocalStorage()) return
    const snapshot: ProctoringTimerSnapshot = {
        referenceId,
        remainingSeconds: Math.max(0, Math.floor(remainingSeconds)),
        updatedAt: now,
    }
    try {
        localStorage.setItem(storageKey(referenceId), JSON.stringify(snapshot))
    } catch {
        // Quota / private mode — ignore.
    }
}

export const clearProctoringTimerSnapshot = (referenceId: string): void => {
    if (!referenceId || !canUseLocalStorage()) return
    try {
        localStorage.removeItem(storageKey(referenceId))
    } catch {
        // Quota / private mode — ignore.
    }
}
